import {
  afterEveryRender,
  Component,
  computed,
  contentChild,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, defer, finalize, Subscription, timeout, TimeoutError } from 'rxjs';
import { KorviuSelectComponent } from '../korviu-select/korviu-select.component';
import { IKorviuListConfig, KorviuListItemTypes } from '../../types/korviu-list.config';
import { ISorting } from '../../types/sorting.interface';
import { IFilter } from '../../types/filter.interface';
import { IPagination } from '../../types/pagination.interface';
import { IFilterSection, IFilterSectionOption } from '../../types/filter-section.interface';
import { ISortSection, ISortSectionOption } from '../../types/sort-section.interface';
import { IGetPaginatedResponse } from '../../types/get-paginated-response.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { KorviuInputComponent } from '../korviu-input/korviu-input.component';
import { KorviuButtonComponent } from '../korviu-button/korviu-button.component';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { ProductListComponent } from './product-list/product-list.component';
import { TransactionListComponent } from './transaction-list/transaction-list.component';
import { MyGanamigosNetworkListComponent } from './my-ganamigos-network-list/my-ganamigos-network-list.component';
import { ReferralListComponent } from './referral-list/referral-list.component';
import { BrandListComponent } from './brand-list/brand-list.component';
import { NzRadioModule } from 'ng-zorro-antd/radio';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { GanarbolListComponent } from './ganarbol-list/ganarbol-list.component';
import { KorviuNoDataComponent } from '../korviu-no-data/korviu-no-data.component';
import { MySpecialForestComponent } from './my-special-forest/my-special-forest.component';
import { RedeemableInFilterComponent } from './redeemable-in-filter/redeemable-in-filter.component';
import { SortOrders } from '../../types/sort-order.enum';
import { SpecialPaymentsComponent } from './special-payments/special-payments.component';
import { TotalContributionListComponent } from './total-contribution-list/total-contribution-list.component';
import { MyForestListComponent } from './my-forest-list/my-forest-list.component';
import { MyForestListSpecialComponent } from './my-forest-list-special/my-forest-list-special.component';
import { RecentActivityComponent } from './recent-activity/recent-activity.component';
import { ListFiltersTemplateDirective } from '../../directives/list-filters.directive';
import { EmptyTemplateDirective } from '../../directives/empty-template.directive';
import { ReferralListGanasociosComponent } from './referral-list-ganasocios/referral-list-ganasocios.component';
import { ProductListV2Component } from 'shared/components/korviu-list/product-list-v2/product-list-v2.component';
import {
  BulkRemoveReferralsModalComponent,
  IBulkRemoveReferralsModalResult
} from './referral-list/bulk-remove-referrals-modal/bulk-remove-referrals-modal.component';

const defaultPagination: IPagination = { page: 1, pageSize: 10 };
const skeletonItems = [1, 2, 3, 4];
const listLoadTimeoutMs = 30_000;

interface IKorviuListFilterForm {
  search: FormControl<string | null>;
  sort: FormControl<string | null>;
}

interface ISelectedFilterChip {
  key: string;
  label: string;
  isLocalizable: boolean;
}

interface ISelectedSortingChip {
  key: string;
  name: string;
  value: string;
  isNameLocalizable: boolean;
  isValueLocalizable: boolean;
}

interface ISortSectionOptionView extends ISortSectionOption {
  nzValue: string;
}

interface ISortSectionView extends Omit<ISortSection, 'options'> {
  options: ISortSectionOptionView[];
}

@Component({
  selector: 'korviu-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzFormModule,
    NzCheckboxModule,
    TranslateModule,
    NzModalModule,
    NzInputModule,
    NzButtonModule,
    NzTagModule,
    NzSkeletonModule,
    NzPaginationModule,
    KorviuInputComponent,
    KorviuButtonComponent,
    ProductListComponent,
    TransactionListComponent,
    MyGanamigosNetworkListComponent,
    ReferralListComponent,
    BrandListComponent,
    KorviuSelectComponent,
    NzRadioModule,
    NzCollapseModule,
    GanarbolListComponent,
    KorviuNoDataComponent,
    MySpecialForestComponent,
    RedeemableInFilterComponent,
    SpecialPaymentsComponent,
    TotalContributionListComponent,
    MyForestListComponent,
    MyForestListSpecialComponent,
    RecentActivityComponent,
    ReferralListGanasociosComponent,
    ProductListV2Component
  ],
  templateUrl: './korviu-list.component.html',
  styleUrls: ['./korviu-list.component.scss']
})
export class KorviuListComponent implements OnInit {
  readonly sortSelect = viewChild<KorviuSelectComponent>('sortSelect');
  readonly filterSelect = viewChild<KorviuSelectComponent>('filterSelect');
  readonly listViewport = viewChild<ElementRef<HTMLElement>>('listViewport');

  private readonly _destroyRef = inject(DestroyRef);
  private readonly _translateService = inject(TranslateService);
  private readonly _modalService = inject(NzModalService);

  readonly config = input.required<IKorviuListConfig>();
  readonly searchChange = output<IFilter | null>();
  readonly sortingChange = output<ISorting | null>();
  readonly filterChange = output<IFilter[]>();

  readonly listFiltersTpl = contentChild<ListFiltersTemplateDirective>(ListFiltersTemplateDirective);
  readonly listFiltersTplRef = computed<TemplateRef<HTMLElement> | null>(
    () => this.listFiltersTpl()?.templateRef || null
  );
  readonly emptyTpl = contentChild<EmptyTemplateDirective>(EmptyTemplateDirective);
  readonly emptyTplRef = computed<TemplateRef<HTMLElement> | null>(() => this.emptyTpl()?.templateRef || null);
  readonly loadError = signal<'timeout' | 'request' | null>(null);
  readonly loadingMore = signal(false);
  readonly loadMoreError = signal(false);

  public readonly KorviuListItemTypes: typeof KorviuListItemTypes = KorviuListItemTypes;
  public readonly skeletonItems = skeletonItems;
  public currentLang: string = this._translateService.currentLang;
  public isLoading = true;
  public filterSectionsIsLoading = false;
  public items: any[] = [];
  public total = 0;
  public totalPages = 0;
  public selectedFilters: ISelectedFilterChip[] = [];
  public selectedSorting: ISelectedSortingChip[] = [];
  public filters: IFilter[] = [];
  public pagination: IPagination = { ...defaultPagination };
  public filterSectionsView: IFilterSection[] = [];
  public sortSectionsView: ISortSectionView[] = [];
  public selectedReferralIds: ReadonlySet<number> = new Set<number>();

  private _sorting: ISorting | null = null;
  private _search: IFilter | null = null;
  private _loadDataSubscription: Subscription | null = null;
  private readonly _tableFilterForm: FormGroup<IKorviuListFilterForm> = new FormGroup<IKorviuListFilterForm>({
    search: new FormControl<string | null>(''),
    sort: new FormControl<string | null>('')
  });

  constructor() {
    afterEveryRender(() => {
      const viewport = this.listViewport()?.nativeElement;
      if (
        viewport &&
        this.config().scrollPagination &&
        !this.loadMoreError() &&
        viewport.clientHeight > 0 &&
        viewport.scrollHeight <= viewport.clientHeight
      ) {
        this.loadMore();
      }
    });
  }

  public get itemRange(): string {
    if (!this.total) {
      return '0-0';
    }

    if (this.config().scrollPagination) {
      return `1-${this.items.length}`;
    }

    const startIndex: number = (this.pagination.page - 1) * this.pagination.pageSize + 1;
    let endIndex: number = this.pagination.page * this.pagination.pageSize;
    endIndex = endIndex > this.total ? this.total : endIndex;
    return `${startIndex}-${endIndex}`;
  }

  get totalLabel(): string {
    const config = this.config();
    const totalText = this._translateService.instant('korviuList.total');
    const localizableName = config.totalLocalizableName || config.totalLocalizableNameFn?.();
    const localizedName = localizableName ? this._translateService.instant(localizableName) : '';

    if (!localizedName) {
      return totalText;
    }

    return this.currentLang === 'en' ? `${totalText} ${localizedName}` : `${localizedName} ${totalText}`;
  }

  public get isSinglePage(): boolean {
    return this.totalPages <= 1;
  }

  public get hasSelectedReferrals(): boolean {
    return this.selectedReferralIds.size > 0;
  }

  public get sorting(): ISorting | null {
    return this._sorting;
  }
  public set sorting(value: ISorting | null) {
    this._setSorting(value);
  }

  public get searchControl(): FormControl<string | null> {
    return this._tableFilterForm.controls.search;
  }

  public get sortControl(): FormControl<string | null> {
    return this._tableFilterForm.controls.sort;
  }

  public ngOnInit(): void {
    const config = this.config();

    this._initFormListeners();
    this._setPagination();
    this._setSortSectionsView();
    this._setFilterSectionsView(config.filterSections);
    this._setLangListener();
    this._setDefaultSorting();
    this._prefillSearchFromConfig();
    this._prefillSortingFromConfig();
    this._prefillFiltersFromConfig();

    if (config.filterSectionsGetFn) {
      this._loadFilterSectionOptions();
    }

    this._loadData();
  }

  public getSortDirectionFromNzValue(value: string): ISorting {
    const [sortBy, sortOrder] = value?.split('|') ?? [];

    return { sortBy, sortOrder: sortOrder as SortOrders };
  }

  public getSortDirectionNzValue(value: ISorting): string {
    return value.sortBy + '|' + value.sortOrder;
  }

  public onDeleteSelectedFilterOrSorting(index: number, isSorting?: boolean): void {
    if (isSorting) {
      this.selectedSorting = [];
      this.sortControl.setValue(null, { emitEvent: false });
      this.sorting = null;
      this.sortingChange.emit(this._sorting);
      this.reloadData(true);

      return;
    }

    const removedFilter = this.selectedFilters[index];

    if (!removedFilter) {
      return;
    }

    this._setFilterCheckedState(removedFilter.key, false);
    this.onFilterChange();
  }

  public onFilterChange(
    changedSection?: IFilterSection,
    changedOption?: IFilterSectionOption,
    checked?: boolean
  ): void {
    if (changedSection && changedOption && checked !== undefined) {
      changedOption.checked = checked;

      if (changedSection.selectionMode === 'single' && checked) {
        changedSection.checkboxModel.forEach((option: IFilterSectionOption) => {
          if (option !== changedOption) {
            option.checked = false;
          }
        });
      }
    }

    const filters: IFilter[] = [];
    const selectedFilters: ISelectedFilterChip[] = [];

    this.filterSectionsView.forEach((section: IFilterSection) =>
      section.checkboxModel.forEach((model: IFilterSectionOption) => {
        if (model.checked) {
          filters.push({ name: section.parameter_name, value: model.parameter_name });
          selectedFilters.push({
            key: this._getFilterChipKey(section.parameter_name, model.parameter_name),
            label: model.localizableName || model.label,
            isLocalizable: !!model.localizableName
          });
        }
      })
    );

    this.selectedFilters = selectedFilters;
    this.filters = filters;
    this.filterChange.emit(this.filters);

    this.reloadData(true, false);
  }

  public onEventsListPageIndexChange(page: number): void {
    this.pagination.page = page;
    this._loadData();
  }

  public onSelectAllReferrals(): void {
    this.selectedReferralIds = new Set<number>(this.items.map(item => item.id));
  }

  public onUnselectAllReferrals(): void {
    this._clearSelectedReferrals();
  }

  public onSelectedReferralIdsChange(selectedReferralIds: ReadonlySet<number>): void {
    this.selectedReferralIds = new Set<number>(selectedReferralIds);
  }

  public onRemoveSelectedReferrals(): void {
    if (!this.selectedReferralIds.size) {
      return;
    }

    const modalRef = this._modalService.create<BulkRemoveReferralsModalComponent, { referralIds: number[] }>({
      nzContent: BulkRemoveReferralsModalComponent,
      nzWidth: 554,
      nzData: { referralIds: [...this.selectedReferralIds] },
      nzCentered: true,
      nzWrapClassName: 'modal-wrap'
    });

    modalRef.afterClose
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((result: IBulkRemoveReferralsModalResult | undefined) => {
        if (!result?.removedCount) {
          return;
        }

        this._clearSelectedReferrals();
        this.reloadData();
      });
  }

  public reloadData(resetPagination?: boolean, closeSelects?: boolean): void {
    if (resetPagination) {
      this._setPagination();
    }
    this._loadData(closeSelects);
  }

  public onListScroll(): void {
    const viewport = this.listViewport()?.nativeElement;
    if (viewport && viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight < 160 && !this.loadMoreError()) {
      this.loadMore();
    }
  }

  public loadMore(): void {
    if (
      !this.config().scrollPagination ||
      this.isLoading ||
      this.loadingMore() ||
      this.items.length >= this.total ||
      this.pagination.page >= this.totalPages
    ) {
      return;
    }
    this.pagination.page++;
    this._loadData(false, true);
  }

  public clearSearchAndFilters(): void {
    this.searchControl.setValue('');
    this._search = null;
    this.filters = [];
    this._applyFiltersToSections([]);
    this._syncSelectedFilters();
    this.searchChange.emit(null);
    this.filterChange.emit([]);
    this.reloadData(true);
  }

  public onDetailsClick(data: unknown): void {
    const detailsFn = this.config().detailsFn;

    if (typeof detailsFn === 'function') {
      detailsFn(data);
    }
  }

  private _initFormListeners(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(1000), takeUntilDestroyed(this._destroyRef))
      .subscribe((value: string | null) => {
        if ((value || null) === (this._search?.value ?? null)) {
          return;
        }
        this._search = value ? { name: 'search', value } : null;
        this.searchChange.emit(this._search);
        this.reloadData(true);
      });

    this.sortControl.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((value: string | null) => {
      this.sorting = value ? this.getSortDirectionFromNzValue(value) : null;
      this.sortingChange.emit(this._sorting);
      // A new order rebuilds the whole result set, so the current page number no longer points at the same rows.
      this.reloadData(true);
    });
  }

  private _prefillSearchFromConfig(): void {
    const config = this.config();

    if (config.search) {
      this._search = config.search;
      this.searchControl.patchValue(this._search?.value, { emitEvent: false });
    }
  }

  private _prefillSortingFromConfig(): void {
    const config = this.config();

    if (config.sorting) {
      this.sorting = config.sorting;
    }
  }

  private _prefillFiltersFromConfig(): void {
    const config = this.config();

    this.filters = config.filters ? [...config.filters] : [];
    this._applyFiltersToSections(this.filters);
    this._syncSelectedFilters();
  }

  private _setDefaultSorting(): void {
    const config = this.config();

    if (config.sorting) {
      return;
    }

    config.sortSections?.forEach((section: ISortSection) => {
      const selectedByDefaultSortSection: ISortSectionOption = section.options?.find(
        (sectionOption: ISortSectionOption) => sectionOption.selectedByDefault
      );
      if (selectedByDefaultSortSection?.sortDirection) {
        this.sorting = selectedByDefaultSortSection.sortDirection;
      }
    });
  }

  private _setPagination(): void {
    this.pagination = { ...(this.config().pagination || defaultPagination) };
  }

  private _setLangListener(): void {
    this._translateService.onLangChange.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(() => {
      this.currentLang = this._translateService.currentLang;
      this._setFilterSectionsView(this.config().filterSections);
    });
  }

  private _loadFilterSectionOptions(): void {
    const config = this.config();
    const filterSectionsGetFn = config.filterSectionsGetFn;

    if (!filterSectionsGetFn) {
      return;
    }

    this.filterSectionsIsLoading = true;
    filterSectionsGetFn()
      .pipe(
        finalize(() => {
          this.filterSectionsIsLoading = false;
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((filterSections: IFilterSection[]) => {
        config.filterSections = filterSections;
        this._applyFiltersToSections(this.filters);
        this._setFilterSectionsView(filterSections);
        this._syncSelectedFilters();
      });
  }

  private _loadData(closeSelects: boolean = true, append = false): void {
    const config = this.config();

    this._loadDataSubscription?.unsubscribe();
    this.loadError.set(null);
    this.loadMoreError.set(false);
    if (append) {
      this.loadingMore.set(true);
    } else {
      if (config.scrollPagination) {
        this.pagination.page = 1;
        this.items = [];
        this.listViewport()?.nativeElement.scrollTo({ top: 0 });
      }
      this._setIsLoading(true);
    }
    if (closeSelects) {
      this._closeSelects();
    }
    this._loadDataSubscription = defer(() =>
      config.getFn(
        this.pagination,
        [...this.filters, this._search].filter((filter: IFilter | null): filter is IFilter => !!filter),
        this._sorting
      )
    )
      .pipe(
        timeout({ first: listLoadTimeoutMs }),
        finalize(() => {
          this._setIsLoading(false);
          this.loadingMore.set(false);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: (data: IGetPaginatedResponse<any>) => {
          this.total = data.count;
          this.totalPages = Math.ceil(this.total / this.pagination.pageSize);
          this._setItems(append ? [...this.items, ...(data?.results || [])] : data?.results || []);
        },
        error: (error: unknown) => {
          if (append) {
            this.pagination.page--;
            this.loadMoreError.set(true);
            return;
          }
          this.loadError.set(error instanceof TimeoutError ? 'timeout' : 'request');
          this._clearSelectedReferrals();
        }
      });
  }

  private _setSorting(value: ISorting | null): void {
    const config = this.config();

    this._sorting = value;
    this.selectedSorting = [];

    if (!this._sorting) {
      this.sortControl.patchValue(null, { emitEvent: false });

      return;
    }

    const selectedSortSection: ISortSection | undefined = config.sortSections?.find((section: ISortSection) =>
      section?.options?.some(
        (option: ISortSectionOption) =>
          option?.sortDirection?.sortBy === this._sorting?.sortBy &&
          option?.sortDirection?.sortOrder === this._sorting?.sortOrder
      )
    );
    const selectedSortOption: ISortSectionOption | undefined = selectedSortSection?.options.find(
      (option: ISortSectionOption) =>
        option?.sortDirection?.sortBy === this._sorting?.sortBy &&
        option?.sortDirection?.sortOrder === this._sorting?.sortOrder
    );

    if (selectedSortSection && selectedSortOption) {
      this.selectedSorting.push({
        key: this.getSortDirectionNzValue(this._sorting),
        name: selectedSortSection.localizableName || selectedSortSection.name || '',
        value: selectedSortOption.localizableName || selectedSortOption.name || '',
        isNameLocalizable: !!selectedSortSection.localizableName,
        isValueLocalizable: !!selectedSortOption.localizableName
      });
    }

    this.sortControl.patchValue(this.getSortDirectionNzValue(this._sorting), { emitEvent: false });
  }

  private _closeSelects(): void {
    this.sortSelect()?.close();
    this.filterSelect()?.close();
  }

  private _setItems(value: any[]): void {
    this._clearSelectedReferrals();
    this.items = value;
  }

  private _clearSelectedReferrals(): void {
    this.selectedReferralIds = new Set<number>();
  }

  private _setIsLoading(value: boolean): void {
    this.isLoading = value;
  }

  private _setSortSectionsView(): void {
    const config = this.config();

    this.sortSectionsView =
      config.sortSections?.map((section: ISortSection) => ({
        ...section,
        options:
          section.options?.map((option: ISortSectionOption) => ({
            ...option,
            nzValue: this.getSortDirectionNzValue(option.sortDirection)
          })) || []
      })) || [];
  }

  private _setFilterSectionsView(filterSections: IFilterSection[] = []): void {
    this.filterSectionsView = filterSections.map((section: IFilterSection) => ({
      ...section,
      checkboxModel: this._getSortedFilterOptions(section.checkboxModel || [])
    }));
  }

  private _getSortedFilterOptions(options: IFilterSectionOption[]): IFilterSectionOption[] {
    if (this.config().doNotSortFilterSections) {
      return [...options];
    }

    return [...options].sort((a: IFilterSectionOption, b: IFilterSectionOption) => {
      const aName: string = a.localizableName ? this._translateService.instant(a.localizableName) : a.label;
      const bName: string = b.localizableName ? this._translateService.instant(b.localizableName) : b.label;

      return aName.localeCompare(bName);
    });
  }

  private _applyFiltersToSections(filters: IFilter[]): void {
    const config = this.config();
    const selectedFilterKeys = new Set(
      filters.map((filter: IFilter) => this._getFilterChipKey(filter.name, filter.value))
    );

    config.filterSections?.forEach((section: IFilterSection) => {
      section.checkboxModel?.forEach((option: IFilterSectionOption) => {
        option.checked = selectedFilterKeys.has(this._getFilterChipKey(section.parameter_name, option.parameter_name));
      });
    });

    this._setFilterSectionsView(config.filterSections);
  }

  private _syncSelectedFilters(): void {
    const selectedFilters: ISelectedFilterChip[] = [];

    this.filterSectionsView.forEach((section: IFilterSection) =>
      section.checkboxModel.forEach((model: IFilterSectionOption) => {
        if (model.checked) {
          selectedFilters.push({
            key: this._getFilterChipKey(section.parameter_name, model.parameter_name),
            label: model.localizableName || model.label,
            isLocalizable: !!model.localizableName
          });
        }
      })
    );

    this.selectedFilters = selectedFilters;
  }

  private _setFilterCheckedState(key: string, checked: boolean): void {
    this.filterSectionsView.forEach((section: IFilterSection) =>
      section.checkboxModel.forEach((option: IFilterSectionOption) => {
        if (this._getFilterChipKey(section.parameter_name, option.parameter_name) === key) {
          option.checked = checked;
        }
      })
    );
  }

  private _getFilterChipKey(filterName: string, filterValue: string): string {
    return `${filterName}:${filterValue}`;
  }
}
